/**
 * Reports API Client
 * Frontend API calls for viewing RSVP reports (admin/caterer read access)
 */

import { apiCall } from '../api';
import type { RSVPReportResponse, RSVPReportParams } from '@/types/api';

function buildReportQueryParams(params: RSVPReportParams): URLSearchParams {
  const queryParams = new URLSearchParams({
    start: params.start,
    end: params.end,
  });

  if (params.mode) {
    queryParams.append('mode', params.mode);
  }

  if (params.locationId !== undefined) {
    queryParams.append('locationId', params.locationId.toString());
  }

  if (params.includeDetails !== undefined) {
    queryParams.append('includeDetails', params.includeDetails.toString());
  }

  return queryParams;
}

/**
 * Fetch RSVP report data for a date range
 *
 * @param params - Report parameters including date range and optional filters
 * @returns Report data with summary, location breakdown, and details
 */
export async function fetchRSVPReport(
  params: RSVPReportParams
): Promise<RSVPReportResponse> {
  const queryParams = buildReportQueryParams(params);

  const response = await apiCall<RSVPReportResponse>(
    `/api/admin/reports/rsvps?${queryParams.toString()}`,
    { method: 'GET' }
  );

  return response;
}

/**
 * Export RSVP report as XLSX file download
 *
 * @param params - Report parameters including date range and optional filters
 * @returns Blob containing XLSX data
 */
export async function exportRSVPReportXLSX(
  params: RSVPReportParams
): Promise<void> {
  const queryParams = buildReportQueryParams(params);

  // Fetch XLSX directly (not using apiCall since it expects JSON)
  const response = await fetch(
    `/api/admin/reports/rsvps/export?${queryParams.toString()}`,
    {
      method: 'GET',
      credentials: 'include',
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({
      error: `HTTP ${response.status}`,
    }));
    throw new Error(error.error || `Export failed with status ${response.status}`);
  }

  // Get the blob and trigger download
  const blob = await response.blob();
  const filename = `rsvp-report-${params.start}-to-${params.end}.xlsx`;

  // Create a download link and click it
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
